import { FormComboBox } from '@/components/forms/form-combo-box';
import { FormInputDate } from '@/components/forms/form-input-date';
import { FormInputTime } from '@/components/forms/form-input-time';
import { FormMultiSelect } from '@/components/forms/form-multi-select';
import { FormRadioGroup } from '@/components/forms/form-radio-group';
import { FormSingleSelect } from '@/components/forms/form-single-select';
import { FormTextArea } from '@/components/forms/form-text-area';
import { Checkbox } from '@/components/ui/checkbox';
import { MultiSelectOption } from '@/components/ui/multi-select';
import { buildNameLikeSelectOptions, comboBoxOptionsOf, emptySelectOption } from '@/components/ui/multi-select-utils';
import {
  coachesTypeaheadSearch,
  useAllLearningPaths,
  useAvailableCoachesSearch,
  useClassTimeOptions,
  userTypeaheadSearch,
} from '@/lib/api-call-hooks';
import { getCoaches } from '@/lib/api-calls';
import { AllSelectTeacherTypes, AllUpdateTeacherTypeValues, CoachesTypeaheadSearchOptions } from '@/lib/coach-utils';
import { formCreateEditOneTimeClassSchema } from '@/lib/student-group-utils';
import {
  distinct,
  formatClassTimeMeetingTimePairLabel,
  FrequentTimeZones,
  isNotNullish,
  OneTimeOnboardingLearningPathNames,
} from '@airreading/common';
import {
  adjustMinimalClassTimeAcrossTZ,
  classEligibilitiesRequiredForLearningPathName,
  CoachStatus,
  encodeClassTimeMeetingTimePair,
  oneTimeClassDurationLabels,
  oneTimeClassDurationOptions,
  GetCoachesModifier,
  MinimalClassTime,
  OnboardingLearningPathName,
  StudentGroupWithScheduleAndLevel,
  STUDENT_GROUP_NOTES_MAX_LENGTH,
} from '@airreading/common/data';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

function uniqueSortedUsCentralStartPlainTimesFromClassTimes(
  classTimes: MinimalClassTime[],
  schoolTimeZone: string,
): { startPlainTime: Temporal.PlainTime; endPlainTime: Temporal.PlainTime }[] {
  const timePairs = classTimes.flatMap((ct) =>
    adjustMinimalClassTimeAcrossTZ(ct, schoolTimeZone, FrequentTimeZones.UsCentral).meetingBlocks.map((mb) => ({
      startPlainTime: mb.startPlainTime,
      endPlainTime: mb.endPlainTime,
    })),
  );
  const unique = timePairs.filter(distinct((tp) => encodeClassTimeMeetingTimePair(tp.startPlainTime, tp.endPlainTime)));
  unique.sort((a, b) => {
    const byStart = Temporal.PlainTime.compare(a.startPlainTime, b.startPlainTime);
    if (byStart !== 0) return byStart;
    return Temporal.PlainTime.compare(a.endPlainTime, b.endPlainTime);
  });
  return unique;
}

function findClassTimeIdByUsCentralMeetingTimePairValue(
  classTimes: MinimalClassTime[],
  schoolTimeZone: string,
  meetingTimePairValue: string,
): string | undefined {
  if (!meetingTimePairValue) return undefined;
  for (const ct of classTimes) {
    const adjusted = adjustMinimalClassTimeAcrossTZ(ct, schoolTimeZone, FrequentTimeZones.UsCentral);
    for (const mb of adjusted.meetingBlocks) {
      if (encodeClassTimeMeetingTimePair(mb.startPlainTime, mb.endPlainTime) === meetingTimePairValue) {
        return ct.id;
      }
    }
  }
  return undefined;
}

export interface CreateEditOneTimeClassFormProps {
  schoolId: string;
  schoolTimeZone: string;
  form: UseFormReturn<z.infer<typeof formCreateEditOneTimeClassSchema>>;
  from?: StudentGroupWithScheduleAndLevel;
}

export const CreateEditOneTimeClassForm = ({
  schoolId,
  schoolTimeZone,
  form,
  from,
}: CreateEditOneTimeClassFormProps) => {
  const queryClient = useQueryClient();
  const { watch, setValue } = form;
  const selectedScheduleId = watch('schedule').value;
  const selectedClassTimeMeetingTimePairValue = watch('classTime').value;
  const overrideTime = watch('overrideTime');
  const overrideStartPlainDate = watch('overrideStartPlainDate');

  const {
    data: schedules,
    classTimes,
    isPending: isLoadingSchedules,
  } = useClassTimeOptions({
    schoolId,
  });
  const selectedSchedule = schedules?.find((s) => s.id === selectedScheduleId);
  const scheduleForCoachFilter = selectedSchedule ?? from?.classTime?.schedule;

  const classTimePlainTimeOptions = useMemo((): MultiSelectOption[] => {
    const classTimes = from?.classTime?.schedule?.classTimes;
    if (!classTimes?.length) return [];
    return uniqueSortedUsCentralStartPlainTimesFromClassTimes(classTimes, schoolTimeZone).map(
      ({ startPlainTime, endPlainTime }) => ({
        label: formatClassTimeMeetingTimePairLabel(startPlainTime, endPlainTime),
        value: encodeClassTimeMeetingTimePair(startPlainTime, endPlainTime),
      }),
    );
  }, [from, schoolTimeZone]);

  const classTimesForCoachLookup = useMemo((): MinimalClassTime[] => {
    if (from?.classTime?.schedule?.classTimes?.length) {
      return from.classTime.schedule.classTimes;
    }
    return schedules?.find((s) => s.id === selectedScheduleId)?.classTimes ?? [];
  }, [from, schedules, selectedScheduleId]);

  const resolvedClassTimeIdForCoachSearch = useMemo(() => {
    if (from?.classTime?.id) {
      return from.classTime.id;
    }
    if (!classTimesForCoachLookup.length || !selectedClassTimeMeetingTimePairValue) {
      return '';
    }
    return (
      findClassTimeIdByUsCentralMeetingTimePairValue(
        classTimesForCoachLookup,
        schoolTimeZone,
        selectedClassTimeMeetingTimePairValue,
      ) ?? ''
    );
  }, [from, classTimesForCoachLookup, schoolTimeZone, selectedClassTimeMeetingTimePairValue]);

  const { learningPaths: allLearningPaths, isPending: isLoadingLearningPaths } = useAllLearningPaths();
  const availableLearningPaths = allLearningPaths.filter(
    (lp) => !OneTimeOnboardingLearningPathNames.includes(lp.name as OnboardingLearningPathName),
  );
  const learningPathOptions: MultiSelectOption[] = availableLearningPaths.map(buildNameLikeSelectOptions);

  const selectedComboLearningPathId = watch('learningPath').value;
  const selectedLearningPath = availableLearningPaths.find((lp) => lp.id === selectedComboLearningPathId);
  const selectableLessons: MultiSelectOption[] = (selectedLearningPath?.learningPathLessons || []).map(
    ({ lesson }) => ({
      label: lesson.name,
      value: lesson.id,
    }),
  );
  const [selectedTeacherType, setSelectedTeacherType] = useState<string | undefined>(
    AllUpdateTeacherTypeValues.Available,
  );

  useEffect(() => {
    const sub = watch((value, { name }) => {
      if (name === 'schedule' && value.classTime?.value) {
        setValue('classTime', emptySelectOption, { shouldDirty: true, shouldValidate: true });
      }

      if (name === 'learningPath') {
        if (value.startingLesson?.value) {
          setValue('startingLesson', emptySelectOption, { shouldDirty: true, shouldValidate: true });
        }
        setValue('coachAvailable', emptySelectOption, { shouldDirty: true, shouldValidate: true });
        setValue('coachAll', emptySelectOption, { shouldDirty: true, shouldValidate: true });
        queryClient.invalidateQueries(getCoaches());
      }
    });
    return () => sub.unsubscribe();
  }, [setValue, watch, form, queryClient]);

  useEffect(() => {
    const sub = watch((value, { name }) => {
      if (name === 'teacherType' && value.teacherType) {
        setValue('coachAvailable', emptySelectOption, { shouldDirty: true, shouldValidate: true });
        setValue('coachAll', emptySelectOption, { shouldDirty: true, shouldValidate: true });

        setSelectedTeacherType(value.teacherType);
        queryClient.invalidateQueries(getCoaches());
      }
    });
    return () => sub.unsubscribe();
  }, [setValue, watch, form, from, queryClient]);

  const learningPathNameForEligibility = selectedLearningPath?.name ?? from?.learningPath?.name ?? '';
  const classEligibilities = classEligibilitiesRequiredForLearningPathName(learningPathNameForEligibility);
  const now = Temporal.Now.plainDateTimeISO(FrequentTimeZones.UsCentral);

  const orgIdForCoachSearch = from?.classTime?.schedule?.school?.org?.id ?? classTimes?.[0]?.schedule?.school?.org?.id;

  const classDatePlainDate =
    overrideStartPlainDate ??
    (from?.overrideStartPlainDate ? Temporal.PlainDate.from(from.overrideStartPlainDate) : undefined);
  const availabilityStartPlainDate = classDatePlainDate ?? Temporal.PlainDate.from(now.toString());
  const availabilityEndPlainDate =
    classDatePlainDate ?? Temporal.PlainDate.from(scheduleForCoachFilter?.endPlainDate ?? now.toString());

  const coachesSearchOptions: GetCoachesModifier =
    scheduleForCoachFilter && resolvedClassTimeIdForCoachSearch && orgIdForCoachSearch
      ? {
          pageIndex: 0,
          filters: {
            availabilities: {
              classTimes: [resolvedClassTimeIdForCoachSearch],
              orgId: orgIdForCoachSearch,
              classEligibilities,
              overrideStartPlainDate: availabilityStartPlainDate,
              overrideEndPlainDate: availabilityEndPlainDate,
              shouldAvoidConflicts: true,
            },
          },
        }
      : {
          pageIndex: 0,
        };

  const coachFilters: CoachesTypeaheadSearchOptions = {
    filters: {
      statuses: [CoachStatus.ready_to_teach],
    },
  };
  const allCoachesSearchOptions: CoachesTypeaheadSearchOptions = {
    shouldDisplayOverallGrade: true,
    ...coachFilters,
  };

  const { data, isLoading: isLoadingCoaches } = useAvailableCoachesSearch(coachesSearchOptions, {
    shouldDisplayOverallGrade: true,
    sortByAvailabilityPriority: true,
  });

  const availableCoaches = useMemo(() => data || [], [data]);

  const teacherSelect =
    selectedTeacherType === AllUpdateTeacherTypeValues.Available ? (
      <FormComboBox
        form={form}
        label={'Available teachers'}
        name={'coachAvailable'}
        placeholder="Select teacher"
        {...comboBoxOptionsOf(availableCoaches)}
        shouldHideCheck={true}
        renderOptionSelected={(option) => (option ? option.label : 'Select a teacher')}
        emptySearchOptionMessage="No available teachers"
        isLoadingKnownOptions={isLoadingCoaches}
      />
    ) : (
      <FormComboBox
        form={form}
        label={'All teachers'}
        name={'coachAll'}
        placeholder="Select teacher"
        {...comboBoxOptionsOf(coachesTypeaheadSearch(queryClient, allCoachesSearchOptions))}
        shouldHideCheck={true}
        renderOptionSelected={(option) => (option ? option.label : 'Select a teacher')}
      />
    );

  return (
    <div className="grid grid-cols-2 gap-2 py-4">
      <FormInputDate name="overrideStartPlainDate" label="Class date" form={form} showClearDateButton={true} />
      <div className="col-span-2">
        <FormComboBox
          form={form}
          label={
            <div className="flex items-center justify-between gap-3 w-full">
              <span>Class time (CT)</span>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="override-time-checkbox"
                  checked={overrideTime}
                  onCheckedChange={(checked) =>
                    setValue('overrideTime', !!checked, { shouldDirty: true, shouldValidate: true })
                  }
                  onClick={(e) => e.stopPropagation()}
                />
                <label htmlFor="override-time-checkbox" className="font-normal cursor-pointer">
                  Override Time
                </label>
              </div>
            </div>
          }
          name="classTime"
          placeholder="Select class times"
          {...comboBoxOptionsOf(classTimePlainTimeOptions)}
          triggerProps={{
            disabled: overrideTime || isLoadingSchedules || classTimePlainTimeOptions.length === 0,
            className: 'w-full',
          }}
        />
      </div>
      {overrideTime && (
        <div className="col-span-2 grid grid-cols-2 gap-2">
          <FormInputTime
            name="overrideStartPlainTime"
            label="Start time (CT)"
            form={form}
            triggerButtonProps={{ className: 'w-full' }}
          />

          <FormSingleSelect
            form={form}
            label="Class duration"
            name="overrideStartDurationInMin"
            options={[...oneTimeClassDurationOptions]}
            renderOption={(value) => oneTimeClassDurationLabels[value] ?? value}
            triggerProps={{ className: 'w-full' }}
          />
        </div>
      )}
      <div className="col-span-2">
        <FormRadioGroup label="Teacher" form={form} name="teacherType" options={AllSelectTeacherTypes} />
      </div>
      <div className="col-span-2">{teacherSelect}</div>
      <div className="col-span-2">
        <FormMultiSelect
          form={form}
          label={'Students'}
          name="students"
          multipleSelectorProps={{
            placeholder: 'Search students by name to add to the group',
            onSearch: userTypeaheadSearch(queryClient, { schools: [schoolId].filter(isNotNullish) }),
          }}
        />
      </div>
      <FormComboBox
        form={form}
        label="Learning path"
        name="learningPath"
        placeholder="Select learning path"
        {...comboBoxOptionsOf(learningPathOptions)}
        triggerProps={{ disabled: isLoadingLearningPaths }}
      />
      <FormComboBox
        form={form}
        label="Starting lesson"
        name="startingLesson"
        placeholder="Select starting lesson"
        {...comboBoxOptionsOf(selectableLessons)}
        triggerProps={{
          disabled: isLoadingLearningPaths,
        }}
      />
      <div className="col-span-2">
        <FormTextArea
          form={form}
          label="Notes"
          name="notes"
          textAreaProps={{
            className: 'resize-none',
            placeholder: `Add a note (max ${STUDENT_GROUP_NOTES_MAX_LENGTH} characters)`,
          }}
        />
      </div>
    </div>
  );
};
